import React, { useLayoutEffect, useMemo, useState } from "react";
import * as d3 from "d3";
import {
  Box,
  Button,
  Collapse,
  Stack
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { ARAGON_COLORS } from "../theme/AragonTheme";

const LOGO_SRC = "/images/1080 x 1080 px Logo 1.png";

function useNavbarViewportModel() {
  const [viewport, setViewport] = useState(() => ({
    width: typeof window === "undefined" ? 1440 : window.innerWidth,
    height: typeof window === "undefined" ? 900 : window.innerHeight,
  }));
  useLayoutEffect(() => {
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const visualViewport = window.visualViewport;
        setViewport({
          width: Math.round(visualViewport?.width || window.innerWidth),
          height: Math.round(visualViewport?.height || window.innerHeight),
        });
      });
    };
    update();
    window.addEventListener("resize", update);
    window.visualViewport?.addEventListener("resize", update);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("resize", update);
    };
  }, []);
  return useMemo(() => {
    const width = viewport.width;
    const heightFactor = d3.scaleLinear().domain([620, 1080]).range([0.9, 1]).clamp(true)(viewport.height);
    const shellInset = Math.round(d3.scaleLinear().domain([320, 1920]).range([10, 28]).clamp(true)(width));
    const shellWidth = Math.round(
      d3.scaleLinear().domain([860, 1920]).range([720, 940]).clamp(true)(width)
    );
    const shellHeight = Math.round(
      d3.scaleLinear().domain([360, 1920]).range([58, 68]).clamp(true)(width) * heightFactor
    );
    const logoHeight = Math.round(
      d3.scaleLinear().domain([360, 1920]).range([34, 44]).clamp(true)(width) * heightFactor
    );
    const navGap = Math.round(d3.scaleLinear().domain([900, 1920]).range([18, 30]).clamp(true)(width));
    const navFont = d3.scaleLinear().domain([900, 1920]).range([12.5, 14.5]).clamp(true)(width);
    const actionHeight = Math.round(
      d3.scaleLinear().domain([768, 1920]).range([38, 44]).clamp(true)(width) * heightFactor
    );
    const ctaWidth = Math.round(d3.scaleLinear().domain([768, 1920]).range([108, 124]).clamp(true)(width));
    return {
      width,
      shellInset,
      shellWidth,
      shellHeight,
      logoHeight,
      navGap,
      navFont,
      actionHeight,
      ctaWidth,
      mobile: width < 860,
    };
  }, [viewport]);
}

function Navbar() {
  const viewport = useNavbarViewportModel();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const scrollToSection = (sectionId) => {
    setMobileOpen(false);
    const target = document.getElementById(sectionId);
    if (!target) {
      return;
    }
    target.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };
  const navigateTo = (path) => {
    setMobileOpen(false);
    navigate(path);
  };
  const navButtonSx = {
    minWidth: 0,
    px: 0,
    color: ARAGON_COLORS.navy,
    textTransform: "none",
    fontSize: `${viewport.navFont}px`,
    fontWeight: 500,
    lineHeight: 1,
    whiteSpace: "nowrap",
    "&:hover": {
      bgcolor: "transparent",
      color: ARAGON_COLORS.burgundy,
    },
  };
  return (
    <Box
      component="header"
      sx={{
        position: "fixed",
        top: `${viewport.shellInset}px`,
        left: "50%",
        width: viewport.mobile
          ? `calc(100% - ${viewport.shellInset * 2}px)`
          : `${viewport.shellWidth}px`,
        transform: "translateX(-50%)",
        zIndex: 1500,
      }}
    >
      <Box
        sx={{
          height: `${viewport.shellHeight}px`,
          borderRadius: `${Math.round(viewport.shellHeight * 0.34)}px`,
          bgcolor: alpha(ARAGON_COLORS.ivory, 0.68),
          border: `1px solid ${alpha(ARAGON_COLORS.ivory, 0.46)}`,
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          boxShadow: `0 12px 34px ${alpha(ARAGON_COLORS.navy, 0.16)}`,
          overflow: "visible",
        }}
      >
        <Box
          sx={{
            position: "relative",
            height: "100%",
            display: "flex",
            alignItems: "center",
            px: { xs: 1.6, sm: 2.2 },
          }}
        >
          <Button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            disableRipple
            aria-label="Back to top"
            sx={{
              minWidth: 0,
              p: 0,
              height: "100%",
              display: "flex",
              alignItems: "center",
              "&:hover": { bgcolor: "transparent" },
            }}
          >
            <Box
              component="img"
              src={LOGO_SRC}
              alt="Aragon"
              sx={{
                height: `${viewport.logoHeight}px`,
                width: "auto",
                maxWidth: { xs: 110, md: 145 },
                objectFit: "contain",
                display: "block",
              }}
            />
          </Button>
          {!viewport.mobile && (
            <Stack
              component="nav"
              direction="row"
              alignItems="center"
              sx={{
                position: "absolute",
                left: "50%",
                top: 0,
                bottom: 0,
                transform: "translateX(-50%)",
                gap: `${viewport.navGap}px`,
              }}
            >
              <Button disableRipple onClick={() => scrollToSection("for-sellers")} sx={navButtonSx}>
                How It Works
              </Button>
              <Button
                disableRipple
                aria-disabled="true"
                sx={{
                  ...navButtonSx,
                  cursor: "default",
                  "&:hover": {
                    bgcolor: "transparent",
                    color: ARAGON_COLORS.navy,
                  },
                }}
              >
                About
              </Button>
              <Button disableRipple onClick={() => scrollToSection("faq")} sx={navButtonSx}>
                FAQ
              </Button>
            </Stack>
          )}
          {!viewport.mobile && (
            <Stack direction="row" spacing={1.3} alignItems="center" sx={{ ml: "auto" }}>
              <Button
                onClick={() => navigateTo("/auth/login")}
                disableRipple
                sx={{
                  height: `${viewport.actionHeight}px`,
                  px: 1.6,
                  color: ARAGON_COLORS.navy,
                  textTransform: "none",
                  fontSize: `${viewport.navFont}px`,
                  fontWeight: 500,
                  "&:hover": {
                    bgcolor: alpha(ARAGON_COLORS.ivory, 0.28),
                    color: ARAGON_COLORS.burgundy,
                  },
                }}
              >
                Log in
              </Button>
              <Button
                onClick={() => navigateTo("/auth/register")}
                variant="contained"
                disableElevation
                sx={{
                  minWidth: `${viewport.ctaWidth}px`,
                  height: `${viewport.actionHeight}px`,
                  borderRadius: `${Math.round(viewport.actionHeight * 0.24)}px`,
                  bgcolor: ARAGON_COLORS.ivory,
                  color: ARAGON_COLORS.navy,
                  textTransform: "none",
                  fontSize: `${viewport.navFont}px`,
                  fontWeight: 700,
                  border: `1px solid ${alpha(ARAGON_COLORS.navy, 0.10)}`,
                  "&:hover": {
                    bgcolor: ARAGON_COLORS.steel,
                    color: ARAGON_COLORS.ivory,
                  },
                }}
              >
                Get started
              </Button>
            </Stack>
          )}
          {viewport.mobile && (
            <Button
              onClick={() => setMobileOpen((current) => !current)}
              aria-label="Toggle navigation"
              aria-expanded={mobileOpen}
              disableRipple
              sx={{
                ml: "auto",
                minWidth: 42,
                width: 42,
                height: 42,
                p: 0,
                borderRadius: "12px",
                color: ARAGON_COLORS.navy,
                bgcolor: alpha(ARAGON_COLORS.ivory, 0.45),
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: "5px",
              }}
            >
              {[0, 1, 2].map((line) => (
                <Box
                  key={line}
                  component="span"
                  sx={{
                    width: 18,
                    height: "1.5px",
                    bgcolor: "currentColor",
                    opacity: mobileOpen && line === 1 ? 0 : 1,
                    transform:
                      mobileOpen && line === 0
                        ? "translateY(6.5px) rotate(45deg)"
                        : mobileOpen && line === 2
                        ? "translateY(-6.5px) rotate(-45deg)"
                        : "none",
                    transition: "transform 150ms ease, opacity 150ms ease",
                  }}
                />
              ))}
            </Button>
          )}
        </Box>
      </Box>
      <Collapse in={mobileOpen && viewport.mobile} timeout={180}>
        <Box
          sx={{
            mt: 1,
            borderRadius: "18px",
            bgcolor: alpha(ARAGON_COLORS.ivory, 0.94),
            border: `1px solid ${alpha(ARAGON_COLORS.navy, 0.12)}`,
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            overflow: "hidden",
          }}
        >
          <Stack sx={{ p: 1.3 }}>
            <Button onClick={() => scrollToSection("for-sellers")} sx={{ ...navButtonSx, justifyContent: "flex-start", px: 1.3, py: 1.2 }}>
              How It Works
            </Button>
            <Button
              aria-disabled="true"
              sx={{ ...navButtonSx, justifyContent: "flex-start", px: 1.3, py: 1.2, cursor: "default" }}
            >
              About
            </Button>
            <Button onClick={() => scrollToSection("faq")} sx={{ ...navButtonSx, justifyContent: "flex-start", px: 1.3, py: 1.2 }}>
              FAQ
            </Button>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button
                onClick={() => navigateTo("/auth/login")}
                sx={{
                  flex: 1,
                  border: `1px solid ${alpha(ARAGON_COLORS.navy, 0.18)}`,
                  color: ARAGON_COLORS.navy,
                  textTransform: "none",
                }}
              >
                Log in
              </Button>
              <Button
                onClick={() => navigateTo("/auth/register")}
                sx={{
                  flex: 1,
                  bgcolor: ARAGON_COLORS.navy,
                  color: ARAGON_COLORS.ivory,
                  textTransform: "none",
                  "&:hover": { bgcolor: ARAGON_COLORS.wine },
                }}
              >
                Get started
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
}

export default Navbar;
